import { useSyncExternalStore } from "react";
import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import { diag, since } from "./diag";

/**
 * Gallery picker for the iOS app (ios/App/App/MediaPickerPlugin.swift).
 *
 * With the web view's file input, iOS re-encodes every picked video before the
 * app gets it, which looks like a frozen picker for up to a minute. The native
 * picker gets the video as stored, right away.
 *
 * Picked photos are read into normal `File`s. Picked videos stay on disk: the
 * app gets a stand-in `File` (right name, type and size, but no contents) and
 * `lib/video-upload.ts` uploads the real file from disk in pieces.
 */

interface NativePickedFile {
  path: string;
  name: string;
  mimeType: string;
  size: number;
  isVideo: boolean;
}

interface MediaPickerPlugin {
  pick(options: { limit: number; types: MediaTypes }): Promise<{ files: NativePickedFile[] }>;
  cancelPick(): Promise<void>;
  deleteFile(options: { path: string }): Promise<void>;
  addListener(event: "pickStarted", listener: () => void): Promise<PluginListenerHandle>;
  addListener(
    event: "pickProgress",
    listener: (data: { fraction: number }) => void,
  ): Promise<PluginListenerHandle>;
}

export type MediaTypes = "all" | "images" | "videos";

const MediaPicker = registerPlugin<MediaPickerPlugin>("MediaPicker");

const localVideoPaths = new WeakMap<File, string>();

// Progress (0-1) of getting picked files ready after the picker closed, or
// null. Shown by VideoUploadBar with a cancel button: an iCloud video has to
// download first, which looked like a frozen app.
let preparing: number | null = null;
const preparingListeners = new Set<() => void>();
function setPreparing(value: number | null) {
  preparing = value;
  preparingListeners.forEach((l) => l());
}

export function useGalleryPreparing(): number | null {
  return useSyncExternalStore(
    (listener) => {
      preparingListeners.add(listener);
      return () => preparingListeners.delete(listener);
    },
    () => preparing,
    () => preparing,
  );
}

/** Stops getting picked files ready; the pick then resolves with no files. */
export function cancelGalleryPreparing() {
  MediaPicker.cancelPick().catch(() => {});
}

/** True in the iOS app (when this build includes the native picker). */
export function canPickNativeMedia(): boolean {
  return Capacitor.getPlatform() === "ios" && Capacitor.isPluginAvailable("MediaPicker");
}

/**
 * Opens the gallery. Resolves with the picked files, or an empty list if the
 * user closed it. `onPreparing` runs once the picker closes and the files are
 * being got ready (usually instant; an iCloud video has to download first).
 */
export async function pickNativeMedia(
  options: { limit?: number; types?: MediaTypes },
  onPreparing?: () => void,
): Promise<File[]> {
  const started = performance.now();
  let preparingSince = 0;
  const listeners = [
    await MediaPicker.addListener("pickStarted", () => {
      preparingSince = performance.now();
      diag("gallery preparing");
      setPreparing(0);
      onPreparing?.();
    }),
    await MediaPicker.addListener("pickProgress", ({ fraction }) => {
      if (preparing !== null) setPreparing(fraction);
    }),
  ];
  try {
    const { files } = await MediaPicker.pick({
      limit: options.limit ?? 1,
      types: options.types ?? "all",
    });
    const result = await Promise.all(files.map(toFile));
    if (files.length > 0) {
      diag("gallery picked", {
        count: files.length,
        videos: files.filter((f) => f.isVideo).length,
        mb: Math.round(files.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024)),
        ms: since(started),
        prepareMs: preparingSince ? since(preparingSince) : 0,
      });
    }
    return result;
  } catch (error) {
    if ((error as { code?: string })?.code === "CANCELLED") {
      diag("gallery preparing cancelled", { ms: preparingSince ? since(preparingSince) : 0 });
      return [];
    }
    diag("gallery pick failed", { message: String((error as Error)?.message ?? error).slice(0, 120) });
    throw error;
  } finally {
    setPreparing(null);
    listeners.forEach((l) => l.remove());
  }
}

function toFileSync(picked: NativePickedFile): File {
  const file = new File([], picked.name, { type: picked.mimeType });
  Object.defineProperty(file, "size", { value: picked.size });
  localVideoPaths.set(file, picked.path);
  return file;
}

async function toFile(picked: NativePickedFile): Promise<File> {
  if (picked.isVideo) return toFileSync(picked);
  try {
    const response = await fetch(Capacitor.convertFileSrc(picked.path));
    const blob = await response.blob();
    return new File([blob], picked.name, { type: picked.mimeType || blob.type });
  } finally {
    MediaPicker.deleteFile({ path: picked.path }).catch(() => {});
  }
}

/** The on-device path of a video from `pickNativeMedia`, or null for a normal File. */
export function getLocalVideoPath(file: File): string | null {
  return localVideoPaths.get(file) ?? null;
}

/** Reads bytes [start, end) of a video from `pickNativeMedia`. */
export async function readLocalVideoChunk(file: File, start: number, end: number): Promise<Blob> {
  const path = getLocalVideoPath(file);
  if (!path) throw new Error("Not a local video");
  const response = await fetch(Capacitor.convertFileSrc(path), {
    headers: { Range: `bytes=${start}-${end - 1}` },
  });
  if (response.status !== 206 && response.status !== 200) {
    throw Object.assign(new Error(`read ${response.status}`), { code: "nn/read-failed" });
  }
  return response.blob();
}

export interface LocalVideoInfo {
  path: string;
  name: string;
  mimeType: string;
  size: number;
}

/** What's needed to find a picked video again after the app restarts. */
export function getLocalVideoInfo(file: File): LocalVideoInfo | null {
  const path = getLocalVideoPath(file);
  return path ? { path, name: file.name, mimeType: file.type, size: file.size } : null;
}

/** The stand-in `File` for a picked video kept from an earlier session. */
export function restoreLocalVideo(info: LocalVideoInfo): File {
  return toFileSync({ ...info, isVideo: true });
}

/** Deletes the on-device copy of a picked video once it's no longer needed. */
export function releaseLocalVideo(file: File) {
  const path = localVideoPaths.get(file);
  if (!path) return;
  localVideoPaths.delete(file);
  MediaPicker.deleteFile({ path }).catch(() => {});
}
